<script setup>
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { mediaUrl } from '@core/utils/mediaUrl'
import moment from 'moment'
import {useUsersStore, useSupportStore, useAppModeStore} from "@/stores"
import {storeToRefs} from "pinia"
import { buildGooglePhotoUrl } from '@core/utils/restaurantImage'
import { canUseRestaurantAdminMode } from '@/utils/appModeEligibility'
import { isRestaurantAccountPath } from '@/utils/restaurantAccountPaths'

const props = defineProps({
  badgeProps: {
    type: Object,
    required: false,
    default: undefined,
  },
  location: {
    type: null,
    required: false,
    default: 'bottom end',
  },
})

const router = useRouter()

const usersStore = useUsersStore()
let myUser = null;
({ myUser} = storeToRefs(usersStore))

const removeNotification = async notificationId => {
  await usersStore.removeNotification(notificationId)
}

const markRead = async notificationIds => {
  for (const id of notificationIds) {
    await usersStore.markNotificationRead(id)
  }
}

const toggleSeen = async notification => {
  if (notification.isSeen) {
    await usersStore.markNotificationUnread(notification.id)
  } else {
    await usersStore.markNotificationRead(notification.id)
  }
}

const menuVisible = ref(false)

const isAllMarkRead = computed(() => myUser.value.notifications.some(item => item.isSeen === false))

const markAllRead = async () => {
  await usersStore.markAllNotificationsRead()
}

const closeMenu = () => {
  menuVisible.value = false
}

const totalUnreadNotifications = computed(() => myUser.value.notifications.filter(item => !item.isSeen).length)

const avatarText = value => {
  if (!value)
    return ''
  const nameArray = value.split(' ')

  return nameArray.map(word => word.charAt(0).toUpperCase()).join('')
}

const getIcon = notification => {
  if(notification.type == 'reservation'){
    return 'mdi-silverware-variant'
  }
  if(notification.type == 'follow'){
    return 'mdi-user'
  }
  if(notification.type == 'happening'){
    return 'emojione-v1:lightning-mood'
  }
  if(notification.type == 'message'){
    return 'mdi-message-text-outline'
  }

  return false
}

const getImage = notification => {
  if(notification.imageId){
    return mediaUrl(notification.imageId, 'thumb')
  }
  if(notification.googlePhotoReference){
    return buildGooglePhotoUrl(notification.googlePhotoReference)
  }

  return false
}

const goToImageTarget = async notification => {
  if (!notification.imageRouterPath) return
  menuVisible.value = false
  if (!notification.isSeen) {
    await usersStore.markNotificationRead(notification.id)
  }
  await router.push(notification.imageRouterPath)
}

const supportStore = useSupportStore()
const appModeStore = useAppModeStore()

/**
 * A notification for a restaurant's team opens a restaurant screen (Kapil,
 * 2026-09-25: "Thomas should get this... in his personal account and
 * restaurant account"). Restaurant screens show only in the restaurant
 * account, so tapping one from the personal account switches to it first —
 * the router would otherwise send them home.
 */
function enterRestaurantAccountFor(path) {
  if (!isRestaurantAccountPath(path)) return
  if (appModeStore.mode === 'restaurantAdmin') return
  if (!canUseRestaurantAdminMode(myUser.value)) return
  appModeStore.setMode('restaurantAdmin', myUser.value?._id)
}

const doAction = async notification => {
  if(notification.actionRouterPath){
    // Redirect old /app-support notifications to the modern messages view
    if (notification.actionRouterPath === '/app-support') {
      try {
        const conv = await supportStore.getOrCreateSupportConversation()
        if (conv?._id) {
          await router.push({ path: '/messages', query: { conv: conv._id } })
        }
      } catch (err) {
        console.error('Failed to open support thread', err)
      }
    } else {
      enterRestaurantAccountFor(notification.actionRouterPath)
      await router.push(notification.actionRouterPath)
    }
  } else if(notification.imageRouterPath){
    // No explicit action target: fall back to the linked profile/restaurant
    // so the entire notification is clickable, not just the avatar.
    await router.push(notification.imageRouterPath)
  }
  menuVisible.value = false

  if (!notification.isSeen){
    await usersStore.markNotificationRead(notification.id)
  }

}

//watch for when menu becomes visible and then goes back to not
watch(menuVisible, async newVal => {
  if(!newVal && myUser.value.notifications.some(n => !n.isSeen)){
    await usersStore.markAllNotificationsRead()
  }
})

const reversedNotifications = computed(() => [...myUser.value.notifications].reverse())
</script>

<template>
  <VBtn
    id="notification-btn"
    icon="true"
    color="secondary"
    variant="plain"
  >
    <VBadge
      dot
      v-bind="props.badgeProps"
      :model-value="myUser.notifications.some(n => !n.isSeen)"
      color="error"
      bordered
      offset-x="1"
      offset-y="1"
    >
      <VIcon icon="ri-notification-2-line" />
    </VBadge>

    <VMenu
      v-model="menuVisible"
      activator="parent"
      width="380"
      :location="props.location"
      offset="15px"
      :close-on-content-click="false"
      height="600"
    >
      <VCard class="d-flex flex-column">
        <!-- 👉 Header -->
        <VCardItem class="notification-section">
          <div class="d-flex align-center gap-2">
            <span class="text-md">
              <b>Notifications</b>
            </span>
            <span
              v-show="!!isAllMarkRead"
              class="text-xs text-primary"
              style="cursor: pointer; white-space: nowrap;"
              @click="markAllRead"
            >
              Mark all read
            </span>
          </div>

          <template #append>
            <VBtn
              icon
              size="x-small"
              variant="text"
              color="default"
              @click="closeMenu"
            >
              <VIcon
                icon="ri-close-line"
                size="20"
              />
            </VBtn>
          </template>
        </VCardItem>

        <VDivider />

        <!-- 👉 Notifications list -->
        <PerfectScrollbar
          :options="{ wheelPropagation: false }"
          style="max-block-size: 27rem"
        >
          <VList class="py-0">
            <template
              v-for="(notification, index) in reversedNotifications"
              :key="notification.id || `${notification.title}-${index}`"
            >
              <VDivider v-if="index > 0" />
              <VListItem
                link
                lines="one"
                min-height="66px"
                class="list-item-hover-class py-3"
                @click="doAction(notification)"
              >
                <!-- Slot: Prepend -->
                <!-- Handles Avatar: Image, Icon, Text -->
                <div class="d-flex align-start gap-3">
                  <VAvatar
                    size="40"
                    :color="notification.color && !getImage(notification) ? notification.color : undefined"
                    :image="getImage(notification) || undefined"
                    :icon="!getImage(notification) ? (getIcon(notification) || undefined) : undefined"
                    :variant="getImage(notification) ? undefined : 'tonal'"
                    :class="{ 'notification-avatar-clickable': !!notification.imageRouterPath }"
                    @click.stop="notification.imageRouterPath ? goToImageTarget(notification) : null"
                  />

                  <div>
                    <h6 class="text-sm mb-1">
                      {{ notification.title }}
                    </h6>
                    <p
                      class="text-xs mb-2"
                      style="letter-spacing: 0.4px !important; line-height: 18px;"
                    >
                      {{ notification.subtitle }}
                    </p>
                    <div class="flex justify-space-between">
                      <p
                        class="text-xs text-disabled mb-0"
                        style="letter-spacing: 0.4px !important; line-height: 18px;"
                      >
                        {{ moment(notification.time).fromNow() }}
                      </p>
                      <!--                      <v-btn -->
                      <!--                        v-if="notification.actionLabel" -->
                      <!--                        @click.stop="doAction(notification)" -->
                      <!--                        variant="tonal" -->
                      <!--                        size="small" -->
                      <!--                        width="fit-content" -->
                      <!--                        density="compact" -->
                      <!--                        class="visible-in-hover profile-header-edit-button" -->
                      <!--                        style="padding-left: 5px !important; padding-right: 5px !important; margin-right: -38px;">{{notification.actionLabel}} -->
                      <!--                      </v-btn> -->
                    </div>
                  </div>

                  <VSpacer />

                  <div class="d-flex flex-column align-end gap-2">
                    <VIcon
                      :color="!notification.isSeen ? 'primary' : '#a8aaae'"
                      :class="`${notification.isSeen ? 'visible-in-hover' : ''} ms-1`"
                      size="10"
                      icon="ri-circle-fill"
                      @click.stop="toggleSeen(notification)"
                    />

                    <div style="block-size: 20px; inline-size: 20px; margin-right:-5px;">
                      <VIcon
                        size="20"
                        icon="ri-close-line"
                        color="secondary"
                        class="visible-in-hover"
                        @click="removeNotification(notification.id)"
                      />
                    </div>
                  </div>
                </div>
              </VListItem>
            </template>

            <VListItem
              v-show="!myUser.notifications.length"
              class="text-center text-medium-emphasis"
              style="block-size: 56px;"
            >
              <VListItemTitle>None</VListItemTitle>
            </VListItem>
          </VList>
        </PerfectScrollbar>

        <!--        <VDivider /> -->

        <!--        &lt;!&ndash; 👉 Footer &ndash;&gt; -->
        <!--        <VCardText -->
        <!--          v-show="myUser.notifications.length" -->
        <!--          class="pa-4" -->
        <!--        > -->
        <!--          <VBtn -->
        <!--            block -->
        <!--            size="small" -->
        <!--          > -->
        <!--            View All Notifications -->
        <!--          </VBtn> -->
        <!--        </VCardText> -->
      </VCard>
    </VMenu>
  </VBtn>
</template>

<style lang="scss">
.notification-section {
  padding: 14px !important;
}

.list-item-hover-class {
  .visible-in-hover {
    display: none;
  }

  &:hover {
    .visible-in-hover {
      display: block;
    }
  }
}

.notification-avatar-clickable {
  cursor: pointer;
}

/* Ensure the close button is always visible on mobile */
@media (max-width: 600px) {
  .list-item-hover-class {
    .visible-in-hover {
      display: block !important;
    }
  }
}
</style>
